// SuspendedProvidersCard v0.1.0 — collapsible card state persisted per visitor.
import type { ProviderItem } from '../lib/api.js';
import { statusPageUrl } from '../lib/statusPageUrl.js';
import ExternalStatusIcon from './ExternalStatusIcon.js';
import CommandPanel from './ui/CommandPanel.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import { useId } from 'react';
import SectionHeader from './ui/SectionHeader.js';
import StatusGlow from './ui/StatusGlow.js';

interface Props {
  providers: ProviderItem[];
}

export default function SuspendedProvidersCard({ providers }: Props) {
  const [expanded, setExpanded] = usePersistedPanelState('panel:suspended-providers', true);
  const contentId = useId();
  if (!providers || providers.length === 0) return null;

  return (
    <CommandPanel className="border border-warn/50">
      <SectionHeader
        title="SUSPENDED DUE TO STATUS UNAVAILABLE"
        right={<span className="text-dim">{providers.length}</span>}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <ul className="divide-y divide-border/40">
          {providers.map((p) => (
            <li key={p.slug} className="px-3 py-2 font-mono text-sm flex items-start gap-3">
              <StatusGlow severity={3} className="mt-0.5 shrink-0">
                SUSPENDED
              </StatusGlow>
              <div className="min-w-0 flex-1">
                <div className="text-text truncate">{p.name}</div>
                <div className="text-xs text-muted leading-relaxed">
                  {p.suspended_reason ?? 'No reachable public status endpoint is available.'}
                </div>
              </div>
              {p.dashboard_url && (
                <ExternalStatusIcon href={statusPageUrl(p)} label={`Open ${p.name} status page`} />
              )}
            </li>
          ))}
        </ul>
      )}
    </CommandPanel>
  );
}
